'use client';

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

interface QuickLoginContextType {
  isOpen: boolean;
  openModal: () => void;
  closeModal: () => void;
  toggleModal: () => void;
}

const QuickLoginContext = createContext<QuickLoginContextType | undefined>(undefined);

export function QuickLoginProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);

  const openModal = useCallback(() => setIsOpen(true), []);
  const closeModal = useCallback(() => setIsOpen(false), []);
  const toggleModal = useCallback(() => setIsOpen(prev => !prev), []);

  // Global keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check for Cmd+K (Mac) or Ctrl+K (Windows/Linux)
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        toggleModal();
      }
      
      // Close on Escape
      if (event.key === 'Escape' && isOpen) {
        closeModal();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, toggleModal, closeModal]);

  return (
    <QuickLoginContext.Provider value={{ isOpen, openModal, closeModal, toggleModal }}>
      {children}
    </QuickLoginContext.Provider>
  );
}

export function useQuickLogin() {
  const context = useContext(QuickLoginContext);
  if (context === undefined) {
    throw new Error('useQuickLogin must be used within a QuickLoginProvider');
  }
  return context;
}